"use client";

import { Beaker, Droplets, Sun, Sprout } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
export default function ColorsAndFinishes() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const metrics = [{
    value: "99.9%",
    label: "PRECISION"
  }, {
    value: "24/7",
    label: "MONITORING"
  }, {
    value: "ISO",
    label: "CERTIFIED"
  }, {
    value: "100%",
    label: "DURABILITY"
  }];
  const protocolSteps = [{
    number: "01",
    title: "MATERIAL SELECTION",
    description: "Industrial-grade substrates sourced from verified suppliers, tested for thermal resistance and chemical stability in harsh environments.",
    icon: Sprout,
    timing: "24 HRS"
  }, {
    number: "02",
    title: "LASER CALIBRATION",
    description: "High-frequency laser systems tuned to specific material depths, ensuring permanent, high-contrast marking for critical identification.",
    icon: Beaker,
    timing: "48 HRS"
  }, {
    number: "03",
    title: "FINISH TREATMENT",
    description: "Protective coating application to prevent oxidation and UV degradation, maintaining legibility under extreme industrial conditions.",
    icon: Sun,
    timing: "72 HRS"
  }, {
    number: "04",
    title: "QUALITY ASSURANCE",
    description: "Multi-point inspection process verifying adherence to safety standards and client specifications before final dispatch.",
    icon: Droplets,
    timing: "12 HRS"
  }];
  return <section data-section-id="4796" id="colors-and-finishes" className="bg-background text-foreground py-20 md:py-28 lg:py-32 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-[1400px] mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-20 md:mb-24 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">
              [ 04 / PRODUCTION ]
            </p>
            <h2 className="font-default font-bold text-accent leading-[0.9] tracking-tighter uppercase !text-[88px]">
              PRODUCTION
              <br />
              STANDARDS
              <span className="text-primary">.</span>
            </h2>
          </div>

          {}
          <div className="lg:col-span-7 lg:pl-8 flex flex-col justify-between gap-12">
            <div className="max-w-[540px]">
              <p className="font-default text-lg md:text-xl leading-relaxed text-foreground mb-6">
                Our manufacturing process bridges engineering precision with industrial reliability. Every label, tag, and plate is crafted using advanced laser technology, ensuring that your critical identification systems remain legible and durable for years.
              </p>
              <p className="font-default text-base leading-relaxed text-muted-foreground">
                We deliver consistent, high-quality results that meet the rigorous demands of modern infrastructure and manufacturing environments.
              </p>
            </div>

            {}
            
          </div>
        </div>

        {}
        <div className="flex items-center gap-6 mb-12 md:mb-16">
          <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground whitespace-nowrap">
            [ WORKFLOW — 4 STAGES ]
          </span>
          <Separator className="flex-1 bg-border" />
        </div>

        {}
        <div ref={cardsRef} className={`transition-all duration-1000 delay-200 ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex gap-6 md:gap-8 overflow-x-auto pb-12 pr-6 -mx-6 px-6 md:mx-0 md:px-0 snap-x snap-mandatory">
            {protocolSteps.map((step, i) => {
            const Icon = step.icon;
            return <div key={i} data-index={i} className="flex items-stretch shrink-0 snap-start">
                  <div className="relative">
                    {}
                    <div className="absolute inset-0 translate-x-3 translate-y-3 bg-primary" aria-hidden="true" />
                    <Card className="relative w-[280px] md:w-[320px] lg:w-[340px] h-full bg-card backdrop-blur-sm border border-border py-0 gap-0 flex flex-col rounded-lg">
                      <CardContent className="p-7 md:p-8 flex flex-col h-full gap-6">
                        {}
                        <div className="flex items-start justify-between">
                          <span className="font-default font-bold text-6xl md:text-7xl leading-none tracking-tighter" style={{
                        WebkitTextStroke: "1.5px var(--accent)",
                        color: "transparent"
                      }}>
                            {step.number}
                          </span>
                          <div className="border border-border p-2.5">
                            <Icon className="size-5 text-accent" strokeWidth={1.5} />
                          </div>
                        </div>

                        {}
                        <h3 className="font-default font-bold text-lg md:text-xl uppercase tracking-tight text-accent leading-tight">
                          {step.title}
                        </h3>

                        {}
                        <p className="font-default text-sm md:text-[15px] leading-relaxed text-muted-foreground flex-1">
                          {step.description}
                        </p>

                        {}
                        
                      </CardContent>
                    </Card>
                  </div>

                  {}
                  {i < protocolSteps.length - 1 && <div className="hidden xl:flex items-center px-4 lg:px-6">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-px bg-border" />
                        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                          NEXT
                        </span>
                      </div>
                    </div>}
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}